import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { ThumbnailList } from '../ThumbnailList';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../Accordion';
import "./StudyItem.css"

const StudyItem = ({
  date,
  description,
  numInstances,
  modalities,
  isActive,
  onClick,
  isExpanded,
  displaySets,
  activeDisplaySetInstanceUIDs,
  onClickThumbnail,
  onDoubleClickThumbnail,
  onClickUntrack,
  viewPreset = 'thumbnails',
  onThumbnailContextMenu,
}: withAppTypes) => {
  const [isSmallScreen, setIsSmallScreen] = React.useState(
    typeof window !== 'undefined' && window.matchMedia
      ? window.matchMedia('(max-width: 1200px)').matches
      : false
  );

  const [isMobileScreen, setIsMobileScreen] = React.useState(
    typeof window !== 'undefined' && window.matchMedia
      ? window.matchMedia('(max-width: 767px)').matches
      : false
  );

  React.useEffect(() => {
    const mq = window.matchMedia('(max-width: 1200px)');
    const handler = (e) => setIsSmallScreen(e.matches);
    if (mq.addEventListener) mq.addEventListener('change', handler);
    else mq.addListener(handler);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', handler);
      else mq.removeListener(handler);
    };
  }, []);

  React.useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)');
    const handler = (e) => setIsMobileScreen(e.matches);
    if (mq.addEventListener) mq.addEventListener('change', handler);
    else mq.addListener(handler);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', handler);
      else mq.removeListener(handler);
    };
  }, []);

  return (
    <Accordion
      type="single"
      collapsible
      onClick={onClick}
      onKeyDown={onClick}
      role="button"
      tabIndex={0}
      defaultValue={isActive ? 'study-item' : undefined}
      className="w-full h-full flex flex-col"
    >
      <AccordionItem value="study-item" className="w-full h-full flex flex-col">
        <AccordionTrigger className={classnames('toggle_boxshadow')}>

          {isSmallScreen ? (
            // ── SMALL / MOBILE: 3-row stacked layout ──────────────────
            <div className="flex flex-col w-full min-w-0 py-[4px] gap-[2px]">
              {/* Row 1: date */}
              <div className="text-white text-[9px] font-medium truncate">
                {date}
              </div>
              {/* Row 2: modalities + instance count */}
              <div className="flex items-center w-full">
                <div className="text-white text-[8px] truncate leading-none">
                  {modalities}
                </div>

                <div className="text-white text-[8px] leading-none ml-[4px] flex-shrink-0">
                  {numInstances}
                </div>
              </div>
              {/* Row 3: description */}
              <div className="text-gray-400 text-[8px] overflow-hidden text-ellipsis whitespace-nowrap w-full">
                {description}
              </div>
            </div>

          ) : (
            // ── DESKTOP: original side-by-side layout ─────────────────
            <div className="flex flex-1 flex-row h-[40px]">
              <div className="flex w-full flex-row items-center justify-between gap-[4px] min-w-0">
                <div className="flex flex-col items-start min-w-0">
                  <div className="text-white truncate text-[12px]">{date}</div>
                  <div className="overflow-hidden text-ellipsis whitespace-nowrap text-white text-[12px] max-w-[160px]">
                    {description}
                  </div>
                </div>
                <div className="flex flex-col items-end flex-shrink-0">
                  <div className="text-white overflow-hidden text-ellipsis whitespace-nowrap text-[12px] max-w-[150px]">
                    {modalities}
                  </div>
                  <div className="text-white text-[12px]">{numInstances}</div>
                </div>
              </div>
            </div>
          )}

        </AccordionTrigger>

        <AccordionContent
          onClick={event => { event.stopPropagation(); }}
          className="w-full p-0 h-full flex flex-col"
        >
          {isExpanded && displaySets && (
            <ThumbnailList
              thumbnails={displaySets}
              activeDisplaySetInstanceUIDs={activeDisplaySetInstanceUIDs}
              onThumbnailClick={onClickThumbnail}
              onThumbnailDoubleClick={onDoubleClickThumbnail}
              onClickUntrack={onClickUntrack}
              viewPreset={viewPreset}
              onThumbnailContextMenu={onThumbnailContextMenu}
            />
          )}
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
};

StudyItem.propTypes = {
  date: PropTypes.string.isRequired,
  description: PropTypes.string,
  modalities: PropTypes.string.isRequired,
  numInstances: PropTypes.number.isRequired,
  trackedSeries: PropTypes.number,
  isActive: PropTypes.bool,
  onClick: PropTypes.func.isRequired,
  isExpanded: PropTypes.bool,
  displaySets: PropTypes.array,
  activeDisplaySetInstanceUIDs: PropTypes.array,
  onClickThumbnail: PropTypes.func,
  onDoubleClickThumbnail: PropTypes.func,
  onClickUntrack: PropTypes.func,
  viewPreset: PropTypes.string,
};

export { StudyItem };